import { validateRut } from '@fdograph/rut-utilities';
import type { InternalRuleItem } from 'async-validator';
import { COMPANY_RUT_MIN_VALUE } from '../constants/documentType.constant';
import { SHAREHOLDER_PERSON_TYPE } from '@/shared/onboardingForms/constants/shareholders.constant';

export const alphanumericRegex = /^[a-zA-Z0-9\sñáéíóúÁÉÍÓÚ]+$/;
export const letterRegex = /^[a-zA-Z\sñáéíóúÁÉÍÓÚ]+$/;
export const spaceRegex = /\s/;
export const unicodeInvisibleCharsRegex = /\p{C}+/gu;
export const companyNameCharsetRegex = /^[a-zA-Z0-9\sñÑáéíóúÁÉÍÓÚüÜ.,&'-]+$/;

export const stringRuleRequired = 'El campo es requerido';

/**
 * Valida que un campo contenga solo caracteres alfanuméricos, espacios y acentos españoles.
 * Esta regla bloquea automáticamente caracteres invisibles ya que no están en el patrón permitido.
 * @param rules - Configuración de la regla de validación
 * @param value - Valor a validar
 * @returns true si es válido, Error si es inválido
 */
export const alphanumericRule = (rules: InternalRuleItem, value: string) => {
  if (!alphanumericRegex.test(value)) {
    return new Error('El campo debe contener sólo letras y números');
  }

  return true;
};

/**
 * Valida que un campo contenga solo letras, espacios y acentos españoles.
 * @param rules - Configuración de la regla de validación
 * @param value - Valor a validar
 * @returns true si es válido, Error si es inválido
 */
export const letterRule = (rules: InternalRuleItem, value: string) => {
  if (!letterRegex.test(value)) {
    return new Error('El campo debe contener sólo letras');
  }

  return true;
};

/**
 * Valida que un campo no contenga espacios en blanco.
 * @param rules - Configuración de la regla de validación
 * @param value - Valor a validar
 * @returns true si es válido, Error si es inválido
 */
export const noSpace = (rules: InternalRuleItem, value: string) => {
  if (spaceRegex.test(value)) {
    return new Error('No se aceptan espacios en blanco');
  }

  return true;
};

/**
 * Valida que un campo contenga solo caracteres numéricos.
 * @param rules - Configuración de la regla de validación
 * @param value - Valor a validar (string o number)
 * @returns true si es válido, Error si es inválido
 */
export const numericRules = (
  rules: InternalRuleItem,
  value: string | number
) => {
  if (isNaN(Number(value))) {
    return new Error('El campo debe contener sólo números');
  }

  return true;
};

/**
 * Valida que un campo no contenga caracteres invisibles.
 * Nota: Esta regla suele ser redundante al usar alphanumericRule,
 * pero puede ser útil para campos que permiten caracteres especiales.
 * @param rules - Configuración de la regla de validación
 * @param value - Valor a validar
 * @returns true si es válido, Error si es inválido
 */
export const noInvisibleCharsRule = (
  rules: InternalRuleItem,
  value: string
) => {
  if (!value?.trim()) return true;

  if (unicodeInvisibleCharsRegex.test(value)) {
    return new Error(
      'Hay caracteres no válidos en el texto. Evite copiar y pegar de otras fuentes.'
    );
  }

  return true;
};

/**
 * Valida que un nombre de empresa no contenga caracteres fuera del charset esperado
 * (ej. homóglifos como la 'А' cirílica, que imita visualmente a la 'A' latina).
 * No transforma ni corrige el valor: solo señala el error para que el usuario lo corrija.
 * Delega en noInvisibleCharsRule primero: un carácter invisible (ej. zero-width space)
 * listado literalmente en el mensaje quedaría ilegible ('' sin nada visible entre comillas).
 * @param rules - Configuración de la regla de validación
 * @param value - Valor a validar
 * @returns true si es válido, Error si contiene caracteres fuera del charset esperado
 */
export const companyNameCharsetRule = (rules: InternalRuleItem, value: string) => {
  if (!value?.trim()) return true;

  const invisibleCharsResult = noInvisibleCharsRule(rules, value);
  if (invisibleCharsResult !== true) return invisibleCharsResult;

  const invalidChars = [...new Set([...value].filter((char) => !companyNameCharsetRegex.test(char)))];

  if (invalidChars.length > 0) {
    const invalidCharsList = invalidChars.map((char) => `'${char}'`).join(', ');
    return new Error(
      `El nombre contiene un carácter no válido (${invalidCharsList}). Verifica que no haya letras de otro alfabeto (ej. cirílico).`
    );
  }

  return true;
};

export const validateRUT = ({
  value,
  personType = SHAREHOLDER_PERSON_TYPE.NATURAL,
  identificationType,
}: {
  value: string;
  personType:
    | (typeof SHAREHOLDER_PERSON_TYPE)[keyof typeof SHAREHOLDER_PERSON_TYPE]
    | null;
  identificationType: string | null;
}) => {
  const isLegalPerson = personType === SHAREHOLDER_PERSON_TYPE.LEGAL;
  let cleanRut = value.replace(/\./g, '').replace(/-/g, '');

  if (!identificationType) {
    throw new Error('Debes seleccionar un tipo de identificación primero');
  }

  if (identificationType !== 'RUT') return true;

  if (!validateRut(cleanRut || '')) {
    throw new Error('El RUT ingresado no es válido');
  }
  cleanRut = cleanRut.replace(/[kK]/g, '0');
  const numericValue = parseFloat(cleanRut);

  if (isLegalPerson && numericValue < COMPANY_RUT_MIN_VALUE) {
    throw new Error('El RUT no se corresponde con el de una empresa');
  }

  if (!isLegalPerson && numericValue >= COMPANY_RUT_MIN_VALUE) {
    throw new Error('El RUT no se corresponde con el de una persona');
  }

  return true;
};
